import { createEffect, createSignal, For } from 'solid-js'
import AccountSidebar from './AccountSidebar'
import HeaderContainer from '/web/shared/aifans/HeaderContainer'
import Footer from '/web/shared/aifans/Footer'
import { aifansSubscriptionApi } from '/web/store/data/aifans-subscription'
import { SubscriptionEvent, TokenTransaction } from '/common/types/aifans-schema'
import { useNavigate, useParams, A } from '@solidjs/router'
import { getCancellationDescription } from '../../../common/aifans/lib/cancellation-util'
import { formatTransactionType } from '/web/shared/aifans/lib/transaction-util'

type TableData = SubscriptionEvent | TokenTransaction

export default function AdminTransactions() {
  const [tableData, setTableData] = createSignal<TableData[]>([])
  const [totalPages, setTotalPages] = createSignal(1)
  const params = useParams()
  const navigate = useNavigate()
  const pageSize = 10

  createEffect(async () => {
    const currentPage = Number(params.page) || 1
    await loadData(currentPage)
  })

  const loadData = async (page: number) => {
    const res = await aifansSubscriptionApi.getAdminTransactions(page, pageSize)
    if (res.result) {
      setTableData(res.result.transactions)
      setTotalPages(Math.ceil(res.result.totalCount / pageSize))
    }
  }

  const changePage = (page: number) => {
    navigate(page === 1 ? '/admin/transactions' : `/admin/transactions/${page}`)
  }

  const formatDate = (dateString: string) => {
    return {
      date: new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(new Date(dateString)),
      time: new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      }).format(new Date(dateString)),
    }
  }

  const renderPagination = () => {
    const current = Number(params.page || '1')
    const total = totalPages()
    const delta = 2 // Number of pages to show before and after current page
    const range: (number | string)[] = []

    // Always show first page
    range.push(1)

    // Calculate start and end of range around current page
    let start = Math.max(2, current - delta)
    let end = Math.min(total - 1, current + delta)

    // Adjust if current page is near the beginning
    if (current - delta <= 2) {
      end = Math.min(total - 1, 1 + 2 * delta)
    }
    // Adjust if current page is near the end
    if (current + delta >= total - 1) {
      start = Math.max(2, total - 2 * delta)
    }

    // Add ellipsis after first page
    if (start > 2) {
      range.push('...')
    }

    // Add range around current page
    for (let i = start; i <= end; i++) {
      range.push(i)
    }

    // Add ellipsis before last page
    if (end < total - 1) {
      range.push('...')
    }

    // Always show last page if there is more than one page
    if (total > 1) {
      range.push(total)
    }

    return (
      <div class="pagination mt-4 flex justify-center gap-2">
        {/* Previous button */}
        <button
          onClick={() => current > 1 && changePage(current - 1)}
          disabled={current === 1}
          class={`rounded px-3 py-1 ${
            current === 1 ? 'cursor-not-allowed opacity-50' : ''
          } bg-gray-600`}
        >
          ←
        </button>

        {/* Page numbers */}
        <For each={range}>
          {(page) =>
            typeof page === 'string' ? (
              <span class="px-2 py-1">...</span>
            ) : (
              <button
                onClick={() => changePage(page)}
                class={`rounded px-3 py-1 ${
                  current === page ? 'bg-cosplay-purple text-white' : 'bg-gray-600'
                }`}
              >
                {page}
              </button>
            )
          }
        </For>

        {/* Next button */}
        <button
          onClick={() => current < total && changePage(current + 1)}
          disabled={current === total}
          class={`rounded px-3 py-1 ${
            current === total ? 'cursor-not-allowed opacity-50' : ''
          } bg-gray-600`}
        >
          →
        </button>
      </div>
    )
  }

  return (
    <>
      <HeaderContainer />
      <main class="payment-history page-bg relative overflow-hidden bg-main pb-20 pt-20 sm:pt-28 md:bg-none md:pb-36">
        <div class="container relative z-20">
          <div class="wrapper gap-8 lg:flex">
            <AccountSidebar />
            <div class="right flex w-full flex-col">
              <div class="head gray-to-tran mb-3 rounded-xl text-2xl font-semibold sm:mb-7 sm:block sm:p-4 sm:text-[28px]">
                <h2>All Transactions</h2>
              </div>
              <div class="gray-to-tran h-full w-full rounded-xl sm:p-6">
                {tableData().length ? (
                  <div class="payment-table mt-6 inline-block w-full overflow-x-auto font-normal">
                    <table class="w-full table-auto">
                      <thead>
                        <tr>
                          <th class="hidden py-3 text-left sm:table-cell">Date</th>
                          <th class="hidden py-3 text-left sm:table-cell">User</th>
                          <th class="py-3 text-left">Type</th>
                          <th class="hidden py-3 text-left sm:table-cell">Character</th>
                          <th class="py-3 pr-4 text-left">Transaction ID</th>
                          <th class="py-3 pr-4 text-left">Member ID</th>
                          <th class="py-3 pr-4 !text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tableData().map((item, i) => {
                          let amount = item.amount ? `$${item.amount.toFixed(2)}` : '$0.00'
                          let tokenSubtext = ''
                          if ('tokenAmount' in item) {
                            tokenSubtext = `${item.tokenAmount} tokens`
                            // amount = item.amount ? `\$${item.amount}` : ''
                          }

                          const characterInfo = item.characterName ? (
                            <A
                              href={`/model/${item.categorySlug}/${item.characterSlug}`}
                              class="text-[#10e0f9] hover:underline"
                            >
                              <div class="text-base">{item.characterName}</div>
                              {item.characterRealName && (
                                <div class="text-xs text-gray-400">{item.characterRealName}</div>
                              )}
                            </A>
                          ) : (
                            'N/A'
                          )

                          return (
                            <tr class={i !== 0 ? 'border-t border-gray-700' : ''}>
                              <td class="hidden py-4 align-top sm:table-cell">
                                <div class="text-base font-medium">
                                  {formatDate(item.date).date}
                                </div>
                                <div class="text-xs text-gray-400">
                                  {formatDate(item.date).time}
                                </div>
                              </td>
                              <td class="hidden py-4 align-top sm:table-cell">
                                {item.username || item.userId}
                              </td>
                              <td class="py-4 align-top">
                                <div class="flex flex-col gap-1">
                                  <div class="font-medium">{formatTransactionType(item.type)}</div>
                                  {'reasonDescription' in item &&
                                    item.type === 'cancellation' &&
                                    item.reasonDescription && (
                                      <div class="text-xs text-gray-400">
                                        {item.reasonDescription}
                                      </div>
                                    )}
                                  {item.type === 'initial_payment' && (
                                    <div class="text-xs text-gray-400">Initial Payment</div>
                                  )}
                                  {item.type === 'recurring_payment' && (
                                    <div class="text-xs text-gray-400">Recurring</div>
                                  )}
                                </div>
                                <div class="flex flex-col gap-1 text-xs text-gray-400 sm:hidden">
                                  <span>
                                    {formatDate(item.date).date} {formatDate(item.date).time}
                                  </span>
                                  <span>{item.username || item.userId}</span>
                                  {item.characterName && (
                                    <A
                                      href={`/model/${item.categorySlug}/${item.characterSlug}`}
                                      class="text-[#10e0f9] hover:underline"
                                    >
                                      {item.characterName}
                                      {item.characterRealName && ` - ${item.characterRealName}`}
                                    </A>
                                  )}
                                </div>
                              </td>
                              <td class="hidden py-4 align-top sm:table-cell">{characterInfo}</td>
                              <td class="hidden py-4 align-top sm:table-cell">
                                {item.transactionId}
                              </td>
                              <td class="hidden py-4 align-top sm:table-cell">{item.memberId}</td>
                              <td class="py-4 pr-4 !text-right align-top">
                                <div class="font-medium text-[#10e0f9]">{amount}</div>
                                {tokenSubtext && (
                                  <div class="text-xs text-gray-400">{tokenSubtext}</div>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                    {renderPagination()}
                  </div>
                ) : (
                  <div class="empty-state py-8 text-center">
                    <p class="text-xl text-gray-400">No transactions available.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  )
}
